import type { Position } from '../nodes/VideoNode';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $wrapNodeInElement, mergeRegister } from '@lexical/utils';
import {
	$createParagraphNode,
	$createRangeSelection,
	$getSelection,
	$insertNodes,
	$isNodeSelection,
	$isRootOrShadowRoot,
	$setSelection,
	COMMAND_PRIORITY_EDITOR,
	COMMAND_PRIORITY_HIGH,
	COMMAND_PRIORITY_LOW,
	createCommand,
	DRAGOVER_COMMAND,
	DRAGSTART_COMMAND,
	DROP_COMMAND,
	LexicalCommand,
	LexicalEditor,
} from 'lexical';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { CAN_USE_DOM } from '../shared/canUseDOM';

import {
	$createVideoNode,
	$isVideoNode,
	VideoNode,
	VideoPayload,
} from '../nodes/VideoNode';
import ButtonA from '../ui/Button';
import { DialogActions } from '../ui/Dialog';
import FileInput from '../ui/FileInput';
import TextInput from '../ui/TextInput';
import { useFileUploader } from '@/hooks/useFileUploader';
import { Box, CircularProgress } from '@mui/material';
import '../ui/Input.css';

export type InsertVideoPayload = Readonly<VideoPayload>;

const getDOMSelection = (targetWindow: Window | null): Selection | null =>
	CAN_USE_DOM ? (targetWindow || window).getSelection() : null;

export const INSERT_VIDEO_COMMAND: LexicalCommand<VideoPayload> = createCommand(
	'INSERT_VIDEO_COMMAND'
);

export function InsertVideoDialog({
	activeEditor,
	onClose,
}: {
	activeEditor: LexicalEditor;
	onClose: () => void;
}): JSX.Element {
	const hasModifier = useRef(false);

	const [isLoad, setIsLoad] = useState(false);
	const [src, setSrc] = useState<any>(null);
	const [file, setFile] = useState<File | undefined>(undefined);
	const [thumbnailSrc, setThumbnailSrc] = useState('');
	const [thumbnailfile, setThumbnailFile] = useState<File | undefined>(
		undefined
	);
	const [altText, setAltText] = useState('');
	const [showCaption] = useState(false);
	const [position, setPosition] = useState<Position>('full');

	const isDisabled = src === '' || thumbnailSrc === '';

	// const handleShowCaptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
	// 	setShowCaption(e.target.checked);
	// };

	const handlePositionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setPosition(e.target.value as Position);
	};

	const loadVideo = (files: FileList | null) => {
		const reader = new FileReader();
		reader.onload = function () {
			// if (typeof reader.result === 'string') {
			// 	setSrc(reader.result);
			// }
			setSrc(reader.result);

			setIsLoad(false);
			return '';
		};
		if (files !== null && files.length > 0) {
			setFile(files[0]);
			// reader.readAsDataURL(files[0]);
			reader.readAsArrayBuffer(files[0]);

			setIsLoad(true);
		}
	};

	const loadThumbnailImage = (files: FileList | null) => {
		const reader = new FileReader();
		reader.onload = function () {
			if (typeof reader.result === 'string') {
				setThumbnailSrc(reader.result);
			}
			setIsLoad(false);
			return '';
		};
		if (files !== null && files.length > 0) {
			setThumbnailFile(files[0]);
			reader.readAsDataURL(files[0]);
			setIsLoad(true);
		}
	};

	useEffect(() => {
		hasModifier.current = false;
		const handler = (e: KeyboardEvent) => {
			hasModifier.current = e.altKey;
		};
		document.addEventListener('keydown', handler);
		return () => {
			document.removeEventListener('keydown', handler);
		};
	}, [activeEditor]);

	// const [
	// 	uploadMedia,
	// 	{ data: uploadMediaResponse, isSuccess: isUploadSuccess },
	// ] = useUploadRichTextMediaMutation();

	const { uploadFile } = useFileUploader();

	const handleOnClick = () => {
		//  upload to server then use url as src

		const upload = async () => {
			if (file) {
				const res = await uploadFile(file);

				if (res) {
					const payload = {
						fileObject: res,
						altText,
						position,
						showCaption,
						src: res.url,
						thumbnailSrc,
					};
					activeEditor.dispatchCommand(INSERT_VIDEO_COMMAND, payload);
					onClose();
				}
			}
		};
		upload();
	};

	return (
		<div style={{ position: 'relative' }}>
			<Box
				sx={{
					position: 'absolute',
					height: '100%',
					width: '100%',
					zIndex: 1,
					backgroundColor: 'rgba(255, 255, 255, 0.7)',
					display: isLoad ? 'flex' : 'none',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<CircularProgress />
			</Box>
			<div style={{ marginBottom: '1em' }}>
				<div className='field-container'>
					<div style={{ color: '#666' }}>Video Upload</div>
					<div className='input-container'>
						<FileInput
							label='Video Upload'
							onChange={loadVideo}
							accept='video/mp4'
							data-test-id='image-modal-file-upload'
						/>

						<div className='input-file-name'>{file?.name}</div>
					</div>
				</div>
			</div>
			<div style={{ marginBottom: '1em' }}>
				<div className='field-container'>
					<div style={{ color: '#666' }}>Thumbnail Upload</div>
					<div className='input-container'>
						<FileInput
							label='Thumbnail Upload'
							onChange={loadThumbnailImage}
							accept='image/*'
							data-test-id='image-modal-file-upload-2'
						/>

						<div className='input-file-name'>{thumbnailfile?.name}</div>
					</div>
				</div>
			</div>
			<div style={{ marginBottom: '1em' }}>
				<TextInput
					label='Alt Text'
					placeholder='Descriptive alternative text'
					onChange={setAltText}
					value={altText}
					data-test-id='image-modal-alt-text-input'
				/>
			</div>

			{/* <Select
				style={{ marginBottom: '1em', width: '290px' }}
				label='Position'
				value={position}
				name='position'
				id='position-select'
				onChange={handlePositionChange}
			>
				<option value='left'>Left</option>
				<option value='right'>Right</option>
				<option value='full'>Full Width</option>
			</Select> */}

			{/* <div className='Input__wrapper'>
				<input
					id='caption'
					type='checkbox'
					checked={showCaption}
					onChange={handleShowCaptionChange}
				/>
				<label htmlFor='caption'>Show Caption</label>
			</div> */}

			<DialogActions>
				<ButtonA
					data-test-id='image-modal-file-upload-btn'
					disabled={isDisabled}
					onClick={() => handleOnClick()}
				>
					Confirm
				</ButtonA>
			</DialogActions>
		</div>
	);
}

export default function VideoPlugin(): JSX.Element | null {
	const [editor] = useLexicalComposerContext();

	useEffect(() => {
		if (!editor.hasNodes([VideoNode])) {
			throw new Error('ImagesPlugin: VideoNode not registered on editor');
		}

		return mergeRegister(
			editor.registerCommand<InsertVideoPayload>(
				INSERT_VIDEO_COMMAND,
				(payload) => {
					const videoNode = $createVideoNode(payload);
					$insertNodes([videoNode]);
					if ($isRootOrShadowRoot(videoNode.getParentOrThrow())) {
						$wrapNodeInElement(videoNode, $createParagraphNode).selectEnd();
					}

					return true;
				},
				COMMAND_PRIORITY_EDITOR
			),
			editor.registerCommand<DragEvent>(
				DRAGSTART_COMMAND,
				(event) => {
					return onDragStart(event);
				},
				COMMAND_PRIORITY_HIGH
			),
			editor.registerCommand<DragEvent>(
				DRAGOVER_COMMAND,
				(event) => {
					return onDragover(event);
				},
				COMMAND_PRIORITY_LOW
			),
			editor.registerCommand<DragEvent>(
				DROP_COMMAND,
				(event) => {
					return onDrop(event, editor);
				},
				COMMAND_PRIORITY_HIGH
			)
		);
	}, [editor]);

	return null;
}

const TRANSPARENT_IMAGE =
	'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
const img = document.createElement('img');
img.src = TRANSPARENT_IMAGE;

function onDragStart(event: DragEvent): boolean {
	const node = getVideoNodeInSelection();
	if (!node) {
		return false;
	}
	const dataTransfer = event.dataTransfer;
	if (!dataTransfer) {
		return false;
	}
	dataTransfer.setData('text/plain', '_');
	dataTransfer.setDragImage(img, 0, 0);
	dataTransfer.setData(
		'application/x-lexical-drag',
		JSON.stringify({
			data: {
				altText: node.__altText,
				caption: node.__caption,
				height: node.__height,
				key: node.getKey(),
				showCaption: node.__showCaption,
				src: node.__src,
				width: node.__width,
			},
			type: 'image',
		})
	);

	return true;
}

function onDragover(event: DragEvent): boolean {
	const node = getVideoNodeInSelection();
	if (!node) {
		return false;
	}
	if (!canDropImage(event)) {
		event.preventDefault();
	}
	return true;
}

function onDrop(event: DragEvent, editor: LexicalEditor): boolean {
	const node = getVideoNodeInSelection();
	if (!node) {
		return false;
	}
	const data = getDragImageData(event);
	if (!data) {
		return false;
	}
	event.preventDefault();
	if (canDropImage(event)) {
		const range = getDragSelection(event);
		node.remove();
		const rangeSelection = $createRangeSelection();
		if (range !== null && range !== undefined) {
			rangeSelection.applyDOMRange(range);
		}
		$setSelection(rangeSelection);
		editor.dispatchCommand(INSERT_VIDEO_COMMAND, data);
	}
	return true;
}

function getVideoNodeInSelection(): VideoNode | null {
	const selection = $getSelection();
	if (!$isNodeSelection(selection)) {
		return null;
	}
	const nodes = selection.getNodes();
	const node = nodes[0];
	return $isVideoNode(node) ? node : null;
}

function getDragImageData(event: DragEvent): null | InsertVideoPayload {
	const dragData = event.dataTransfer?.getData('application/x-lexical-drag');
	if (!dragData) {
		return null;
	}
	const { type, data } = JSON.parse(dragData);

	if (type !== 'image') {
		return null;
	}

	return data;
}

declare global {
	interface DragEvent {
		rangeOffset?: number;
		rangeParent?: Node;
	}
}

function canDropImage(event: DragEvent): boolean {
	const target = event.target;
	return !!(
		target &&
		target instanceof HTMLElement &&
		!target.closest('code, span.editor-image') &&
		target.parentElement &&
		target.parentElement.closest('div.ContentEditable__root')
	);
}

function getDragSelection(event: DragEvent): Range | null | undefined {
	let range;
	const target = event.target as null | Element | Document;
	const targetWindow =
		target == null
			? null
			: target.nodeType === 9
			? (target as Document).defaultView
			: (target as Element).ownerDocument.defaultView;
	const domSelection = getDOMSelection(targetWindow);
	if (document.caretRangeFromPoint) {
		range = document.caretRangeFromPoint(event.clientX, event.clientY);
	} else if (event.rangeParent && domSelection !== null) {
		domSelection.collapse(event.rangeParent, event.rangeOffset || 0);
		range = domSelection.getRangeAt(0);
	} else {
		throw Error('Cannot get the selection when dragging');
	}

	return range;
}
